import { Callout, InfoTip } from '@dev-spendesk/grapes';
import React from 'react';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { type AccountingSoftware } from 'modules/bookkeep/integration/status';
import { type ExpenseAccount } from 'modules/bookkeep/settings/accounting';
import { type InfiniteQueryState } from 'src/core/api/queryState';
import { SearchFilter } from 'src/core/common/components/SearchFilter';
import FEATURES from 'src/core/constants/features';

import { ExpenseAccountTable } from './ExpenseAccountTable';
import { ExpenseAccountsSkeletonTable } from './ExpenseAccountsSkeletonTable';
import { getExpenseAccountsDescriptionI18nKey } from './translations';

interface Props {
  search: string;
  expenseAccountQueryState: InfiniteQueryState<ExpenseAccount[]>;
  integration: AccountingSoftware;
  setSearch: (value: string) => void;
  handleToggleAvailabilityExpenseAccount: (
    expenseAccount: ExpenseAccount,
  ) => Promise<void>;
}

export const ExpenseAccountPullSection = ({
  expenseAccountQueryState,
  integration,
  search,
  setSearch,
  handleToggleAvailabilityExpenseAccount,
}: Props) => {
  const { t } = useTranslation('global');
  const hasHideAccountsFeature = useFeature(FEATURES.TMP_HIDE_EXPENSE_ACCOUNTS);

  const hasHideAccountsFeatureOrAvailableIntegration =
    hasHideAccountsFeature ||
    [
      'Xero',
      'Netsuite',
      'Sage100',
      'ACD',
      'Odoo',
      'SageIntacct',
      'MicrosoftDynamics',
      'ExactOnlineFr',
      'ExactOnlineBe',
      'ExactOnlineNl',
      'DatevRewe',
    ].includes(integration);

  return (
    <div id="expense-accounts" className="w-full max-w-lg self-center">
      <div className="flex items-center gap-8">
        <div className="flex-grow">
          <h3 className="IntegrationAccountingPage__section-title heading-l">
            {t('bookkeep.integrations.settings.sectionExpenseAccounts')}
          </h3>
          <p className="IntegrationAccountingPage__section-description body-m">
            {t(getExpenseAccountsDescriptionI18nKey(integration))}
            {hasHideAccountsFeatureOrAvailableIntegration && (
              <InfoTip
                className="ml-4"
                content={t(
                  'bookkeep.integrations.settings.expenseAccountsDescriptionTooltip',
                )}
              />
            )}
          </p>
        </div>
        <div>
          <SearchFilter
            search={search}
            setSearch={(localSearch) => setSearch(localSearch)}
            placeholder={t(
              'bookkeep.integrations.settings.expenseAccountsTable.searchPlaceholder',
            )}
          />
        </div>
      </div>
      <QuerySuspense
        queryState={expenseAccountQueryState}
        loading={<ExpenseAccountsSkeletonTable />}
        fallback={() => <ExpenseAccountsErrorWarning />}
      >
        {(expenseAccounts) => (
          <div className="my-32">
            <ExpenseAccountTable
              expenseAccounts={expenseAccounts}
              fetchNextPage={expenseAccountQueryState.fetchNextPage}
              hasHideAccountsFeatureOrAvailableIntegration={
                hasHideAccountsFeatureOrAvailableIntegration
              }
              hasFooter={expenseAccountQueryState.hasNextPage}
              isLoading={
                expenseAccountQueryState.status === 'loading' &&
                !expenseAccountQueryState.isFetchingNextPage
              }
              isFetchingMore={expenseAccountQueryState.isFetchingNextPage}
              onSwitch={(row) => handleToggleAvailabilityExpenseAccount(row)}
            />
          </div>
        )}
      </QuerySuspense>
    </div>
  );
};

const ExpenseAccountsErrorWarning = () => {
  const { t } = useTranslation('global');

  return (
    <Callout
      title={t('bookkeep.integrations.settings.errorLoading')}
      variant="alert"
    />
  );
};
